import _ from "lodash";
import * as PropTypes from "prop-types";
import React, {useCallback, useEffect, useState} from "react";
import {toast} from "react-toastify";
import {SYSTEMADMIN} from "../../consts/funktionaleRollen";
import http, {handleApiException, handleValidatedApiException, isApiCancel, useHttp} from "../../services/HttpService";
import {logger} from "../../services/LogService";
import {
    getAlleOrganisationenEndpoint,
    getOrganisationenByIdEndpoint,
    getOrganisationenRegisterEndpoint,
    getOrganisationsHistorie,
    postOrganisationenExportEndpointConfiguration,
    postOrganisationFusionEndpoint,
} from "../../services/OrganisationsService";
import {createCalculatedColumn, createColumn, createComponentColumn, createSelectRowComponentColumn} from "../../utils/ColumnUtils";
import {useForm} from "../../utils/FormUtils";
import {userHasAnyRole} from "../../utils/SecurityUtils";
import {requiredDate, requiredObject, requiredString} from "../../utils/Validator";
import EAButton from "../EAButton";
import EAButtonBar from "../EAButtonBar";
import EADialog from "../EADialog";
import EATable from "../EATable";
import EATextField from "../EATextField";
import AuditInfoDialog from "../historie/AuditInfoDialog";
import EAHistorySelector from "../historie/EAHistorySelector";
import EATableActionButton from "../tableComponents/EATableActionButton";
import EADownloadButton from "../EADownloadButton";
import {useConfirmation} from "../ConfirmationDialog/ConfirmationDialogProvider";
import {confirmDeleteOption} from "../ConfirmationDialog/confirmationDialogOptions";
import CircularProgress from "@material-ui/core/CircularProgress";
import {useHttpSortById} from "../../utils/usePreformattedHttp";
import {changeFieldValueIfPresent} from "../../utils/ObjectUtils";
import EACheckbox from "../EACheckbox";
import "../../styles/components/organisationListe.css";

function TableDialog({
                         open,
                         data,
                         formFactory,
                         values,
                         setValues,
                         fusionsFormFactory,
                         onFusion,
                         fusionLoading,
                         onClickOk,
                         onClickCancel,
                         saving,
                     }) {
    const [fusionsmodusAktiv, setFusionsmodusAktiv] = useState(false);
    const [organisationen] = useHttpSortById(getOrganisationenRegisterEndpoint);
    const [historisch, setHistorisch] = useState(false);
    const [{data: historyData, loading: historyLoading}] = useHttp(getOrganisationsHistorie(data.id), {manual: !data.id});
    const getDialogTitle = useCallback(() => {
        if (fusionsmodusAktiv) {
            return data.bezeichnung + " fusionieren mit...";
        }
        return "Organisation " + (data.id ? "bearbeiten" : "erstellen");
    }, [fusionsmodusAktiv, data.id, data.bezeichnung]);


    const buildEditContent = useCallback(() => {
        return (
            <div className="gridl" style={{gridTemplateColumns: "repeat(6,1fr)"}}>
                {formFactory.renderCheckbox("status", "status", "Aktiv", historisch, {})}
                {formFactory.renderCheckbox("abwasserbeseitigungspflicht", "abwasserbeseitigungspflicht", "Abwasserbeseitigungspflichtig", historisch, {
                    style: {gridColumn: "span 5"},
                })}
                {formFactory.renderInput("bezeichnung", "bezeichnung", "Bezeichnung", "text", historisch, {
                    style: {gridColumnEnd: "span 6"},
                })}
                {formFactory.renderInput("adresse.strasse", "adresse.strasse", "Straße", "text", historisch, {
                    style: {gridColumn: "span 5"},
                })}
                {formFactory.renderInput("adresse.hausnummer", "adresse.hausnummer", "Haus-Nr.", "text", historisch)}
                {formFactory.renderInput("adresse.plz", "adresse.plz", "PLZ", "text", historisch)}
                {formFactory.renderInput("adresse.ort", "adresse.ort", "Ort", "text", historisch, {
                    style: {gridColumn: "span 5"},
                })}
                {formFactory.renderInput("abgabenummer", "abgabenummer", "Abgabenummer", "text", historisch, {
                    style: {gridColumn: "span 3"},
                })}
                {formFactory.renderInput("organisationsId", "organisationsId", "OrganisationsId", "text", true, {
                    style: {gridColumn: "span 3"},
                })}
            </div>
        );
    }, [formFactory, historisch]);

    const buildFusionsContent = useCallback(() => {
        return (
            <div className="gridl" style={{gridTemplateColumns: "1fr max-content", justifyContent: "flex-end"}}>
                {fusionsFormFactory.renderAutocomplete(
                    "destinationOrganisationId",
                    "destinationOrganisationId",
                    "Fusionieren mit...",
                    organisationen.data.filter((val) => val.id !== data.id),
                    null,
                    "id",
                    organisationen.loading,
                    false,
                    false,
                    {
                        style: {gridColumn: "span 2"},
                        getOptionLabel: (o) =>
                            `${o.bezeichnung} - ${o.adresse.strasse} ${o.adresse.hausnummer}, ${o.adresse.plz} ${o.adresse.ort}`,
                    }
                )}
                {fusionsFormFactory.renderDatePicker("mergeDate", "mergeDate", "Fusion tritt in Kraft ab", false, {
                    style: {gridColumn: 2},
                    disableFuture: true,
                })}
                {fusionsFormFactory.renderCheckbox("includePersonen", "includePersonen", "Personendaten ebenfalls übertragen", false, {
                    style: {gridColumn: 2},
                })}
            </div>
        );
    }, [fusionsFormFactory, organisationen.data, organisationen.loading, data.id]);
    const backgroundColor = historisch ? {background: "#FFFFFF", filter: "sepia()"} : {};
    return (
        <EADialog
            style={backgroundColor}
            open={open}
            titleChildren={getDialogTitle()}
            content={
                <div className={"gridl"} style={{width: "651px"}}>
                    {(saving || fusionLoading) && <CircularProgress style={{justifySelf: "center"}}/>}
                    {!(saving || fusionLoading) && (!fusionsmodusAktiv ? buildEditContent() : buildFusionsContent())}
                </div>
            }
            buttons={
                (!saving && !fusionsmodusAktiv && (
                    <EAButtonBar style={{width: "100%", margingLeft: "10px"}}>
                        <div style={{marginRight: "auto"}}>
                            {data.id && (
                                <EAHistorySelector
                                    data={historyData}
                                    loading={historyLoading}
                                    additionalColumns={[
                                        createCalculatedColumn(
                                            "status",
                                            (row) => {
                                                return row?.status;
                                            },
                                            "Status",
                                            {padding: "2px"}
                                        ),
                                    ]}
                                    onClickSelect={(row) => {
                                        row.status = row.status === true || row.status === "AKTIV";
                                        if (row.timestamp !== "aktuell") {
                                            setValues({...row, history: true});
                                            setHistorisch(true);
                                        } else {
                                            setValues({...row});
                                            setHistorisch(true);
                                        }
                                    }}
                                    onCancel={() => {
                                        setHistorisch(false);
                                        data.status = data.status === true || data.status === "AKTIV";
                                        setValues({...data});
                                    }}
                                />
                            )}
                        </div>
                        {data.id && (
                            <EAButton
                                id="btnFusionierenStarten"
                                label="Fusionieren mit..."
                                disabled={historisch}
                                onClick={() => setFusionsmodusAktiv(true)}
                            />
                        )}
                        <EAButton id={"btnAbbrechen"} data-cy={"btnAbbrechen"} label={"Abbrechen"} onClick={onClickCancel}/>
                        <EAButton
                            id={"btnOk"}
                            data-cy={"btnOk"}
                            color="primary"
                            label={"Übernehmen"}
                            disabled={historisch}
                            onClick={() => {
                                onClickOk(_.cloneDeep(values));
                            }}
                        />
                    </EAButtonBar>
                )) ||
                (fusionsmodusAktiv && (
                    <EAButtonBar style={{width: "100%", margingLeft: "10px"}}>
                        <EAButton
                            id={"btnAbbrechen"}
                            data-cy={"btnAbbrechen"}
                            label={"Abbrechen"}
                            disabled={fusionLoading}
                            loading={fusionLoading}
                            onClick={() => setFusionsmodusAktiv(false)}
                        />
                        <EAButton
                            id={"btnFusionieren"}
                            data-cy={"btnFusionieren"}
                            color="primary"
                            label={"Fusionieren"}
                            disabled={fusionLoading}
                            loading={fusionLoading}
                            onClick={() => onFusion()}
                        />
                    </EAButtonBar>
                ))
            }
        />
    );
}

TableDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    saving: PropTypes.bool.isRequired,
    data: PropTypes.any.isRequired,
    formFactory: PropTypes.any.isRequired,
    onClickCancel: PropTypes.func.isRequired,
    onClickOk: PropTypes.func.isRequired,
};

const getInitialObject = () => {
    return {
        id: null,
        bezeichnung: null,
        status: true,
        adresse: {hausnummer: null, ort: null, strasse: null, plz: null},
        abgabenummer: null,
        organisationsId: null,
    };
};

const schema = {
    bezeichnung: requiredString(),
    adresse: {strasse: requiredString(), hausnummer: requiredString(), plz: requiredString(), ort: requiredString()},
};

const fusionsSchema = {
    destinationOrganisationId: requiredObject({}),
    mergeDate: requiredDate(),
};

const OrganisationenComponent = () => {
    const [saving, setSaving] = useState(false);
    const [organisationLoader, refetchOrganisation] = useHttp(getOrganisationenByIdEndpoint(), {manual: true});
    const [selected, setSelected] = useState([]);
    const [{loading: deleteLoading}, deleteOrganisation] = useHttp("", {manual: true});
    const [filteredData, setFilteredData] = useState([]);
    const [filter, setFilter] = useState("");
    const [filterAktiv, setFilterAktiv] = useState(true);
    const [filterInaktiv, setFilterInaktiv] = useState(true);
    const [filterAbwasserbeseitigungspflichtiger, setFilterAbwasserbeseitigungspflichtiger] = useState("");
    const [{data, loading}, refetch] = useHttp(getAlleOrganisationenEndpoint);
    const confirm = useConfirmation();

    // Historie
    const [auditInfoDialogOpen, setAuditInfoDialogOpen] = useState(false);
    const [auditInfoDialogViolation, setAuditInfoDialogViolation] = useState({art: "MISSING", faultyAuditInfo: {}});

    const {values, setValues, setErrors, validate, formFactory} = useForm({readonly: isDisabled(), schema});
    const {
        setValues: setFusionsValues,
        setErrors: setFusionsErrors,
        formFactory: fusionsFormFactory,
        submitLoading: fusionLoading,
        submit: fusionieren,
    } = useForm({
        schema: fusionsSchema,
    });
    const [dlgOpen, setDlgOpen] = useState(false);

    useEffect(() => {
        if (data) {
            logger.debug("Loaded Organistationen", data);
            setFilteredData([
                ...data.filter((item) => {
                    return (
                        (
                            !filter ||
                            item?.bezeichnung?.toString().toLowerCase().includes(filter.toLowerCase()) ||
                            item?.adresse?.strasse?.toString().toLowerCase().includes(filter.toLowerCase()) ||
                            item?.adresse?.hausnummer?.toString().toLowerCase().includes(filter.toLowerCase()) ||
                            item?.adresse?.plz?.toString().toLowerCase().includes(filter.toLowerCase()) ||
                            item?.adresse?.ort?.toString().toLowerCase().includes(filter.toLowerCase())
                        ) && (
                            (filterAktiv && item?.status === "AKTIV") ||
                            (filterInaktiv && item?.status === "INAKTIV")
                        ) && (
                            (item?.abwasserbeseitigungspflicht) === true ||
                            (!filterAbwasserbeseitigungspflichtiger)
                        )
                    );
                }),
            ]);
        }
    }, [data, filter, filterAktiv, filterInaktiv, loading, filterAbwasserbeseitigungspflichtiger]);

    useEffect(() => {
        if (dlgOpen) {
            setFusionsValues({mergeDate: new Date(Date.now())});
            setFusionsErrors({});
        }
    }, [dlgOpen, setFusionsValues, setFusionsErrors]);

    /**
     * Validierungsfehler zurücksetzten wenn Dialog geöffnet wird
     */
    useEffect(() => {
        setErrors({});
    }, [dlgOpen, setErrors])

    useEffect(() => {
        if (organisationLoader.data && !organisationLoader.loading) {
            organisationLoader.data.status = organisationLoader.data.status === "AKTIV";
            setValues(_.cloneDeep(organisationLoader.data));
        }
    }, [setValues, organisationLoader.data, organisationLoader.loading]);

    function handleEdit(index) {
        if (isDisabled()) {
            return;
        }
        refetchOrganisation({url: getOrganisationenByIdEndpoint(index)}).catch((e) =>
            handleApiException(e, "ERROR loading Organisation by ID")
        );
        setErrors(null);
        setDlgOpen(true);
    }

    function handleDelete(index) {
        confirm(
            confirmDeleteOption(
                "die Organisation und alle zugewiesenen Personen und Anwenderkonten",
                () => {
                    deleteOrganisation({
                        url: getOrganisationenByIdEndpoint(filteredData.find((o) => o.id === index).id),
                        method: "DELETE",
                    })
                        .then(() => {
                            toast.success("Gelöscht");
                            setValues(getInitialObject());
                            refetch();
                        })
                        .catch((e) => {
                            if (!isApiCancel(e)) {
                                if (e.response?.status === 400 && e.response.data) {
                                    toast.error(e.response.data);
                                } else {
                                    handleApiException(e, "Fehler beim Löschen");
                                }
                            }
                        });
                },
                () => {
                }
            )
        );
    }

    function prepareForUpload(organisation) {
        if (organisation.status !== "AKTIV" && organisation.status !== "INAKTIV") {
            organisation.status = organisation.status ? "AKTIV" : "INAKTIV";
        }
        return organisation;
    }

    const requestAuditInfo = (violation) => {
        setAuditInfoDialogOpen(true);
        setAuditInfoDialogViolation(violation);
    };

    function save(dataToSave, auditInfo = null) {
        const error = validate();
        if (error) {
            return;
        }
        dataToSave = prepareForUpload(dataToSave);
        if (auditInfo) {
            dataToSave.auditInfo = {...auditInfo};
        }
        logger.debug("Saving data", dataToSave);
        setSaving(true);
        if (dataToSave.id) {
            http.put(getAlleOrganisationenEndpoint + "/" + dataToSave.id, dataToSave)
                .then(() => {
                    toast.success("Gespeichert");
                    refetch();
                    setValues(getInitialObject());
                    setDlgOpen(false);
                    setSaving(false);
                })
                .catch((e) => {
                    setSaving(false);
                    if (e?.response?.data?.validationErrors) {
                        setErrors(e?.response?.data?.validationErrors);
                        const errorMessage = Object.values(e.response.data.validationErrors)[0];
                        toast.warn("Validierung fehlgeschlagen: " + errorMessage);
                        return;
                    } else if (e.response?.data?.auditInfoViolation) {
                        requestAuditInfo(e.response.data);
                        return;
                    }
                    handleApiException(e, "Fehler beim Speichern");
                });
        } else {
            http.post(getAlleOrganisationenEndpoint + "/", dataToSave)
                .then(() => {
                    toast.success("Gespeichert");
                    refetch();
                    setValues(getInitialObject());
                    setDlgOpen(false);
                    setSaving(false);
                })
                .catch((e) => {
                    setSaving(false);
                    logger.debug(e.response);
                    if (e?.response?.data?.validationErrors) {
                        setErrors(e?.response?.data?.validationErrors);
                        const errorMessage = Object.values(e.response.data.validationErrors)[0];
                        toast.warn("Validierung fehlgeschlagen: " + errorMessage);
                        return;
                    }
                    handleApiException(e, "Fehler beim Speichern");
                });
        }
    }

    function isDisabled() {
        return !userHasAnyRole([SYSTEMADMIN]) || anyLoading();
    }

    function anyLoading() {
        return loading || deleteLoading;
    }

    const columns = [
        createSelectRowComponentColumn("organisationenTable", "id", data, selected, setSelected),
        createComponentColumn(
            "actions",
            (row, index) => {
                if (isDisabled()) return <div/>;
                return (
                    <div className="eatabwrapper">
                        <EATableActionButton id={"edit-" + index} iconName="ico-edit">
                            <span
                                onClick={() => handleEdit(row.id)}
                                className={"w-100"}
                                style={{
                                    height: "100%",
                                    display: "block",
                                }}
                            />
                        </EATableActionButton>
                        <EATableActionButton id={"delete-" + index} iconName="ico-delete">
                            <span
                                onClick={() => handleDelete(row.id)}
                                className={"w-100"}
                                style={{
                                    height: "100%",
                                    display: "block",
                                }}
                            />
                        </EATableActionButton>
                    </div>
                );
            },
            "",
            null,
            {padding: 0, width: "50px", minWidth: "50px", maxWidth: "50px"}
        ),
        createColumn("bezeichnung", "Bezeichnung"),
        createColumn("adresse.strasse", "Straße"),
        createColumn("adresse.hausnummer", "Haus-Nr."),
        createColumn("adresse.plz", "PLZ"),
        createColumn("adresse.ort", "Ort"),
        createComponentColumn(
            "isAbwasserbeseitigungspflicht",
            (row) => {
                if (row.abwasserbeseitigungspflicht) return <div className={"tableActionButton icon ico-check"}/>;
                return <div className={"tableActionButton icon ico-kreuz"}/>;
            },
            "Abwasserbeseitigungspflicht"
        ),
        createComponentColumn(
            "status",
            (row) => {
                if (row.status === "AKTIV") return <div className={"tableActionButton icon ico-check"}/>;
                return <div className={"tableActionButton icon ico-kreuz"}/>;
            },
            "Status"
        ),
    ];

    const prepareFusion = useCallback(
        (value) => {
            changeFieldValueIfPresent(value, "destinationOrganisationId", "id");
            value.sourceOrganisationId = values.id;
            return value;
        },
        [values.id]
    );

    const handleFusion = useCallback(() => {
        confirm({
            title: "Fusion bestätigen",
            description: `Sind Sie sicher, dass Sie die Organisation in die ausgewählte Organisation integrieren möchten?`,
            onSubmit: () => {
                fusionieren(prepareFusion, postOrganisationFusionEndpoint, "POST")
                    .then(() => {
                        refetch();
                        setValues(getInitialObject());
                        setDlgOpen(false);
                        toast.success("Die Organisationen wurden erfolgreich fusioniert.");
                    })
                    .catch((e) => handleValidatedApiException(e, "Fehler bei der Fusion zweier Organisationen"));
            },
            onClose: () => {
            },
        });
    }, [fusionieren, confirm, prepareFusion, refetch, setValues]);

    return (
        <div className={"gridl tabsheet-content-margin"}>
            <AuditInfoDialog
                open={auditInfoDialogOpen}
                onClose={() => setAuditInfoDialogOpen(false)}
                violation={auditInfoDialogViolation}
                onAuditInfoSet={(aud) => save(values, aud)}
                onVerwerfen={() => {
                    setAuditInfoDialogOpen(false);
                    refetchOrganisation().catch((e) => handleApiException(e, "Error on AuditInfoDialog.onVerwerfen"));
                }}
            />
            {dlgOpen && !anyLoading() && (
                <TableDialog
                    open={dlgOpen}
                    saving={saving || organisationLoader.loading}
                    data={values.id ? {...organisationLoader.data} : {}}
                    setValues={setValues}
                    values={values}
                    formFactory={formFactory}
                    onClickOk={(dataToSave) => {
                        setValues(dataToSave);
                        save(dataToSave, null);
                    }}
                    onClickCancel={() => setDlgOpen(false)}
                    fusionsFormFactory={fusionsFormFactory}
                    fusionLoading={fusionLoading}
                    onFusion={handleFusion}
                />
            )}
            <div className="gridl organisationListe-filter">
                <EATextField
                    id={"searchField"}
                    disabled={anyLoading()}
                    loading={anyLoading()}
                    value={filter}
                    label={"Suche"}
                    onChange={({currentTarget}) => {
                        setFilter(currentTarget.value);
                    }}
                    style={{paddingBottom: "10px"}}
                />
                <EACheckbox
                    id="organisationActive"
                    checked={filterAktiv}
                    onChange={(event, value) => (setFilterAktiv(value))}
                >
                    aktiv
                </EACheckbox>
                <EACheckbox
                    id="organisationInactive"
                    checked={filterInaktiv}
                    onChange={(event, value) => (setFilterInaktiv(value))}
                >
                    inaktiv
                </EACheckbox>
                <EACheckbox
                    id="organisationAbwasserbeseitigungspflichtige"
                    onChange={(event, value) => (setFilterAbwasserbeseitigungspflichtiger(value))}
                >
                    nur Abwasserbeseitigungspflichtige
                </EACheckbox>
            </div>
            <EATable
                id={"organisationenTable"}
                loading={anyLoading()}
                initialSortColumn="bezeichnung"
                columns={columns}
                data={filteredData}
            />
            {!anyLoading() && !isDisabled() && (
                <EAButtonBar>
                    <EADownloadButton
                        id="exportOrganisationen"
                        label="Excel-Export"
                        disabled={!selected || selected.length === 0}
                        config={postOrganisationenExportEndpointConfiguration(selected)}
                        style={{marginRight: "auto"}}
                    />
                    <EAButton
                        id={"addOrganisation"}
                        loading={anyLoading()}
                        label={"Hinzufügen"}
                        onClick={() => {
                            setValues(getInitialObject());
                            setDlgOpen(true);
                        }}
                        style={{justifySelf: "end"}}
                        color="primary"
                    />
                </EAButtonBar>
            )}
        </div>
    );
};

export default OrganisationenComponent;
